import * as React from 'react';
import { Popover } from '@base-ui-components/react/popover';
import { Info } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { FieldContext } from './FieldContext';
import styles from './Field.module.css';

/**
 * Поле формы: подпись над контролом, подсказка или ошибка под ним.
 *
 *     <Field label="Название">
 *       <Input value={name} onChange={…} />
 *     </Field>
 *
 * Работает с любым контролом внутри: Input, Textarea, Select, Combobox,
 * DatePicker. Field связывает подпись с контролом по id и помечает контрол
 * `data-in-field` — по этой метке поле выравнивает текст внутри по подписи.
 *
 * @use обёртка контрола в форме: подпись над полем, подсказка, состояние ошибки.
 */
export interface FieldProps {
  label: React.ReactNode;
  children: React.ReactElement;
  hint?: React.ReactNode;
  hintAs?: 'text' | 'tooltip';
  error?: React.ReactNode;
  required?: boolean;
  floated?: boolean;
  className?: string;
}

export const Field: React.FC<FieldProps> = ({ label, children, hint, hintAs, error, required, className }) => {
  const generatedId = React.useId();
  const child = children as React.ReactElement<{ id?: string }>;
  const controlId = child.props.id ?? generatedId;

  const control = React.cloneElement(child, {
    id: controlId,
    'data-in-field': '',
  } as React.Attributes & { id: string });

  const context = React.useMemo(() => ({ controlId }), [controlId]);

  const hintTip = hintAs === 'tooltip' && hint != null && hint !== '';
  const labelNode = (
    <label htmlFor={controlId} className={styles.label}>
      {label}
      {required && (
        <span className={styles.required} aria-hidden>
          *
        </span>
      )}
    </label>
  );
  const below = error ?? (hintTip ? null : hint);

  return (
    <div className={cn(styles.field, className)}>
      {hintTip ? (
        <div className={styles.labelRow}>
          {labelNode}
          <HintTip hint={hint} />
        </div>
      ) : (
        labelNode
      )}
      <FieldContext.Provider value={context}>{control}</FieldContext.Provider>
      {below != null && below !== '' && below !== false && (
        <span className={cn(styles.hint, error && styles.error)}>{below}</span>
      )}
    </div>
  );
};

const HintTip: React.FC<{ hint: React.ReactNode }> = ({ hint }) => (
  <Popover.Root>
    <Popover.Trigger openOnHover delay={150} closeDelay={120} className={styles.hintTrigger} aria-label="Пояснение">
      <Info size={14} aria-hidden />
    </Popover.Trigger>
    <Popover.Portal>
      <Popover.Positioner side="top" align="start" sideOffset={6} collisionPadding={12} className={styles.hintPositioner}>
        <Popover.Popup className={styles.hintPopup}>{hint}</Popover.Popup>
      </Popover.Positioner>
    </Popover.Portal>
  </Popover.Root>
);
